import { createElement } from "react"
import { QuizIntroStep } from "../components/steps/quiz-intro-step"
import type { StepComponentProps } from "../components/steps/step-contract"
import type { QuizConfig, QuizStep } from "../types"

/** Funnel param that selects the shared first screen. Absent or unknown → none. */
export const QUIZ_INTRO_PARAM = "intro_v"

export const QUIZ_INTRO_VARIANTS = ["prediction"] as const

export type QuizIntroVariant = (typeof QUIZ_INTRO_VARIANTS)[number]

const QUIZ_INTRO_STEP_ID = "quiz-intro"

function isQuizIntroVariant(value: string): value is QuizIntroVariant {
  return (QUIZ_INTRO_VARIANTS as readonly string[]).includes(value)
}

/**
 * Which intro to prepend, if the funnel asked for a known one.
 * Pure — side-effect-free for tests.
 */
export function resolveQuizIntro(
  params: Record<string, string | undefined>,
): QuizIntroVariant | null {
  const value = params[QUIZ_INTRO_PARAM]?.trim()
  if (value && isQuizIntroVariant(value)) return value
  return null
}

function introStep(): QuizStep {
  return {
    id: QUIZ_INTRO_STEP_ID,
    type: "cover",
    title: "When you will have a baby?",
    countsTowardProgress: false,
    fullBleed: true,
    component: (props: StepComponentProps) =>
      createElement(QuizIntroStep, { onAdvance: props.onAdvance }),
  }
}

/**
 * Prepend the shared intro when `intro_v` is `prediction`. Quiz configs stay
 * untouched — the returned config is a shallow copy, or the same reference
 * when no intro applies.
 */
export function withQuizIntro(
  config: QuizConfig,
  params: Record<string, string | undefined>,
): QuizConfig {
  if (!resolveQuizIntro(params)) return config
  if (config.steps[0]?.id === QUIZ_INTRO_STEP_ID) return config
  return { ...config, steps: [introStep(), ...config.steps] }
}
